/**
 * API Service for Admin Panel
 * Handles all HTTP requests to the backend
 * Matches the structure from the existing web app but with admin-specific endpoints
 */

import axios, { AxiosInstance, AxiosError } from 'axios';
import { API_CONFIG, LIVE_CONTROL_ENDPOINTS } from '../config/api';
import { tokenManager } from '../utils/tokenManager';
import { AppConfig, MarketOrderingItem, normalizeAppConfig } from '../types/appConfig';
import { AdminLiveBet, AdminLiveStats, AdminLiveUserHistory } from '../types/adminLive';

class ApiService {
  public readonly baseURL: string;
  private client: AxiosInstance;
  private onUnauthorized: (() => void) | null = null;
  private onSuspended: (() => void) | null = null;

  constructor() {
    this.baseURL = API_CONFIG.baseURL;
    this.client = axios.create({
      baseURL: API_CONFIG.baseURL,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
      maxRedirects: 0, // Don't follow redirects (they convert POST to GET)
    });

    // Add request interceptor to include auth token
    this.client.interceptors.request.use(
      (config) => {
        const token = tokenManager.getAccessToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
          // Also set lowercase version for compatibility
          config.headers.authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Add response interceptor to handle 401 Unauthorized and 403 Suspended
    this.client.interceptors.response.use(
      (response) => {
        // Handle 401 in successful responses (since we removed validateStatus)
        const status = response.status;
        const responseData = response.data as { error?: string; message?: string } | undefined;
        
        if (status === 401 && this.onUnauthorized) {
          this.onUnauthorized();
        }
        
        // Check for suspended account (403 with Account Suspended message)
        if (status === 403 && responseData?.error === 'Account Suspended' && this.onSuspended) {
          this.onSuspended();
        }
        
        return response;
      },
      (error: AxiosError) => {
        const status = error.response?.status;
        const responseData = error.response?.data as { error?: string; message?: string } | undefined;
        
        if (status === 401 && this.onUnauthorized) {
          this.onUnauthorized();
        }
        
        // Check for suspended account (403 with Account Suspended message)
        if (status === 403 && responseData?.error === 'Account Suspended' && this.onSuspended) {
          this.onSuspended();
        }
        
        return Promise.reject(error);
      }
    );
  }

  setUnauthorizedCallback(callback: () => void) {
    this.onUnauthorized = callback;
  }

  setSuspendedCallback(callback: () => void) {
    this.onSuspended = callback;
  }

  private handleError(error: unknown): string {
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError<{
        error?: string;
        message?: string;
        code?: string;
        errors?: Array<{ path?: string[]; field?: string; message: string }>;
      }>;

      const responseData = axiosError.response?.data;
      const status = axiosError.response?.status;

      if (responseData) {
        // Handle validation errors
        if (responseData.errors && Array.isArray(responseData.errors)) {
          const errorMessages = responseData.errors.map((err) => {
            const field = err.path?.join('.') || err.field || 'field';
            return `${field}: ${err.message}`;
          });
          return errorMessages.join(', ');
        }

        // Handle error message
        if (responseData.message) {
          return responseData.message;
        }

        if (responseData.error) {
          return responseData.error;
        }
      }

      // Handle HTTP status codes
      if (status === 401) {
        return 'Unauthorized. Please log in again.';
      }
      if (status === 403) {
        return 'Access forbidden. You do not have permission to perform this action.';
      }
      if (status === 404) {
        return 'Resource not found.';
      }
      if (status === 500) {
        return 'Server error. Please try again later.';
      }

      return axiosError.message || 'An unexpected error occurred';
    }

    if (error instanceof Error) {
      return error.message;
    }

    return 'An unexpected error occurred';
  }

  // Health check
  async healthCheck(): Promise<{ status: string; timestamp: string; services: { database: string; redis: string } }> {
    try {
      const response = await this.client.get('/health');
      return response.data;
    } catch (error) {
      throw new Error(this.handleError(error));
    }
  }

  // Admin authentication endpoints
  async adminLogin(email: string, password: string): Promise<{
    message: string;
    admin: { id: string; email: string; name: string; role: string; active?: boolean };
    token: string;
    refreshToken: string;
    requiresFirstLogin?: boolean;
    requires2FA?: boolean;
    twofaMethod?: 'app' | 'sms';
  }> {
    try {
      const response = await this.client.post('/api/v1/admin/auth/login', { email, password });
      return response.data;
    } catch (error) {
      throw new Error(this.handleError(error));
    }
  }

  async getCurrentAdmin(): Promise<{
    admin: { id: string; email: string; name: string; role: string; active: boolean; last_login_at: string | null };
  }> {
    try {
      const response = await this.client.get('/api/v1/admin/auth/me');
      return response.data;
    } catch (error) {
      throw new Error(this.handleError(error));
    }
  }

  async adminLogout(refreshToken: string): Promise<{ message: string }> {
    try {
      const response = await this.client.post('/api/v1/admin/auth/logout', { refreshToken });
      return response.data;
    } catch (error) {
      throw new Error(this.handleError(error));
    }
  }

  // First login setup
  async completeFirstLogin(data: {
    password: string;
    pin: string;
    twofaMethod?: 'app' | 'sms';
    twofaSecret?: string;
    twofaCode?: string;
    phoneNumber?: string;
  }): Promise<{ message: string; token: string; refreshToken: string }> {
    try {
      const response = await this.client.post('/api/v1/admin/auth/first-login-setup', data);
      return response.data;
    } catch (error) {
      throw new Error(this.handleError(error));
    }
  }

  async checkFirstLoginStatus(): Promise<{
    requiresFirstLogin: boolean;
    passwordSet: boolean;
    pinSet: boolean;
    twofaEnabled: boolean;
  }> {
    try {
      const response = await this.client.get('/api/v1/admin/auth/first-login-status');
      return response.data;
    } catch (error) {
      throw new Error(this.handleError(error));
    }
  }

  // 2FA endpoints
  async start2FASetup(method: 'app' | 'sms', phoneNumber?: string): Promise<{
    secret?: string;
    qrCode?: string;
    manualEntryKey?: string;
    smsCodeSent?: boolean;
  }> {
    try {
      const response = await this.client.post('/api/v1/admin/auth/2fa/setup/start', { method, phoneNumber });
      return response.data;
    } catch (error) {
      throw new Error(this.handleError(error));
    }
  }

  async verify2FA(code: string, method: 'app' | 'sms' | 'backup', phoneNumber?: string): Promise<{
    message: string;
    token: string;
    refreshToken: string;
  }> {
    try {
      const response = await this.client.post('/api/v1/admin/auth/verify-2fa', { code, method, phoneNumber });
      return response.data;
    } catch (error) {
      throw new Error(this.handleError(error));
    }
  }

  // Password reset
  async requestPasswordReset(email: string): Promise<{ message: string }> {
    try {
      const response = await this.client.post('/api/v1/admin/auth/password-reset/request', { email });
      return response.data;
    } catch (error) {
      throw new Error(this.handleError(error));
    }
  }

  async verifyPasswordResetToken(token: string): Promise<{ valid: boolean; adminId?: string; email?: string }> {
    try {
      const response = await this.client.post('/api/v1/admin/auth/password-reset/verify', { token });
      return response.data;
    } catch (error) {
      throw new Error(this.handleError(error));
    }
  }

  async resetPassword(token: string, newPassword: string): Promise<{ message: string }> {
    try {
      const response = await this.client.post('/api/v1/admin/auth/password-reset/complete', { token, newPassword });
      return response.data;
    } catch (error) {
      throw new Error(this.handleError(error));
    }
  }

  // PIN endpoints
  async verifyPIN(pin: string): Promise<{ message: string; actionToken: string }> {
    try {
      const response = await this.client.post('/api/v1/admin/auth/verify-pin', { pin });
      return response.data;
    } catch (error) {
      throw new Error(this.handleError(error));
    }
  }

  async setPIN(pin: string): Promise<{ message: string }> {
    try {
      const response = await this.client.post('/api/v1/admin/auth/pin/set', { pin });
      return response.data;
    } catch (error) {
      throw new Error(this.handleError(error));
    }
  }

  async changePIN(currentPin: string, newPin: string): Promise<{ message: string }> {
    try {
      const response = await this.client.post('/api/v1/admin/auth/pin/change', { currentPin, newPin });
      return response.data;
    } catch (error) {
      throw new Error(this.handleError(error));
    }
  }

  async getPINStatus(): Promise<{ pinSet: boolean }> {
    try {
      const response = await this.client.get('/api/v1/admin/auth/pin/status');
      return response.data;
    } catch (error) {
      throw new Error(this.handleError(error));
    }
  }

  async createAdmin(data: {
    name: string;
    email: string;
    role: 'finance' | 'support' | 'compliance' | 'content' | 'risk';
  }): Promise<{
    admin: { id: string; email: string; name: string; role: string };
    temporaryPassword?: string;
    message: string;
  }> {
    try {
      const response = await this.client.post('/api/v1/admin/accounts', data);
      return response.data;
    } catch (error) {
      throw new Error(this.handleError(error));
    }
  }

  async getAllAdmins(): Promise<{
    admins: Array<{ id: string; email: string; name: string; role: string; active: boolean; status?: 'pending_activation' | 'active' | 'inactive'; created_at: string; last_login_at: string | null }>;
  }> {
    try {
      const response = await this.client.get('/api/v1/admin/accounts');
      return response.data;
    } catch (error) {
      throw new Error(this.handleError(error));
    }
  }

  async updateAdminRole(adminId: string, role: string): Promise<{ message: string }> {
    try {
      const response = await this.client.put(`/api/v1/admin/accounts/${adminId}/role`, { role });
      return response.data;
    } catch (error) {
      throw new Error(this.handleError(error));
    }
  }

  async getAllUsers(filters?: {
    filter?: 'all' | 'verified' | 'pending' | 'banned';
    search?: string;
    country?: string;
    riskLevel?: 'high' | 'med' | 'low';
    status?: 'active' | 'limited' | 'frozen' | 'locked';
    limit?: number;
    offset?: number;
  }): Promise<{
    users: Array<{
      id: string;
      email: string;
      username: string;
      email_verified: boolean;
      kyc_status?: 'none' | 'pending' | 'approved' | 'rejected' | 'resubmission_required';
      country?: string | null;
      created_at: string;
      deleted_at: string | null;
      frozen_at: string | null;
    }>;
    total: number;
  }> {
    try {
      const response = await this.client.get('/api/v1/admin/users', { params: filters });
      return response.data;
    } catch (error) {
      throw new Error(this.handleError(error));
    }
  }

  async getUserById(userId: string): Promise<{
    user: {
      id: string;
      email: string;
      username: string;
      email_verified: boolean;
      kyc_status?: 'none' | 'pending' | 'approved' | 'rejected' | 'resubmission_required';
      created_at: string;
      updated_at: string;
      deleted_at: string | null;
      device_fingerprints: Array<{
        device_id: string;
        device_type: string;
        created_at: string;
      }>;
      ip_addresses: string[];
      frozen_at: string | null;
      freeze_reason: string | null;
      share_earn_brand?: string | null;
      share_earn_cohort?: string | null;
    };
  }> {
    try {
      const response = await this.client.get(`/api/v1/admin/users/${userId}`);
      return response.data;
    } catch (error) {
      throw new Error(this.handleError(error));
    }
  }

  async getUserShareEarnTags(userId: string): Promise<{
    shareEarnBrand: string | null;
    shareEarnCohort: string | null;
  }> {
    try {
      const response = await this.client.get(`/api/v1/admin/users/${userId}/share-earn-tags`);
      return response.data;
    } catch (error) {
      throw new Error(this.handleError(error));
    }
  }

  async searchAdminUsers(
    q: string,
    limit = 20
  ): Promise<{
    users: Array<{ id: string; email: string; username: string }>;
  }> {
    const response = await this.client.get('/api/v1/admin/users/search', {
      params: { q, limit },
    });
    return response.data;
  }

  // ───────────── versus AI (assistant management) ─────────────
  // Soul = the AI system prompt (single source of truth; takes effect next session).
  async getAISoul(): Promise<{
    systemPrompt: string;
    enabled: boolean;
    model: string;
    updatedAt: string;
    updatedByAdminId: string | null;
  }> {
    const response = await this.client.get('/api/v1/admin/ai/settings');
    return response.data;
  }

  async updateAISoul(systemPrompt: string): Promise<{
    systemPrompt: string;
    updatedAt: string;
    updatedByAdminId: string | null;
  }> {
    const response = await this.client.patch('/api/v1/admin/ai/settings', { systemPrompt });
    return response.data;
  }

  async getAISoulHistory(limit = 20): Promise<{
    history: Array<{ id: string; systemPrompt: string; savedAt: string; savedByAdminId: string | null }>;
  }> {
    const response = await this.client.get('/api/v1/admin/ai/soul/history', { params: { limit } });
    return response.data;
  }

  // Dedicated AI notes about a user — stored separately from admin/KYC notes.
  async getAIUserNotes(userId: string): Promise<{
    userId: string;
    username: string | null;
    email: string | null;
    notes: string;
    updatedAt: string | null;
    updatedBy: string | null;
  }> {
    const response = await this.client.get('/api/v1/admin/ai/user-notes', { params: { userId } });
    return response.data;
  }

  async updateAIUserNotes(userId: string, notes: string): Promise<{
    userId: string;
    notes: string;
    updatedAt: string | null;
    updatedBy: string | null;
  }> {
    const response = await this.client.put('/api/v1/admin/ai/user-notes', { userId, notes });
    return response.data;
  }

  async patchUserShareEarnTags(
    userId: string,
    body: { shareEarnBrand?: string | null; shareEarnCohort?: string | null }
  ): Promise<{
    message: string;
    shareEarnBrand: string | null;
    shareEarnCohort: string | null;
  }> {
    try {
      const response = await this.client.patch(`/api/v1/admin/users/${userId}/share-earn-tags`, body);
      return response.data;
    } catch (error) {
      throw new Error(this.handleError(error));
    }
  }

  async updateAdminStatus(adminId: string, active: boolean): Promise<{ message: string }> {
    try {
      const response = await this.client.patch(`/api/v1/admin/accounts/${adminId}/status`, { active });
      return response.data;
    } catch (error) {
      throw new Error(this.handleError(error));
    }
  }

  async freezeUser(userId: string, reason: string, notes?: string): Promise<void> {
    try {
      await this.client.post(`/api/v1/admin/users/${userId}/freeze`, { reason, notes });
    } catch (error) {
      throw new Error(this.handleError(error));
    }
  }

  async unfreezeUser(userId: string, reason: string, notes?: string): Promise<void> {
    try {
      await this.client.post(`/api/v1/admin/users/${userId}/unfreeze`, { reason, notes });
    } catch (error) {
      throw new Error(this.handleError(error));
    }
  }

  async createMarket(marketData: any): Promise<{ market: any }> {
    try {
      const response = await this.client.post('/api/v1/admin/markets', marketData);
      return response.data;
    } catch (error) {
      throw new Error(this.handleError(error));
    }
  }

  async getAllMarkets(filters?: {
    status?: string;
    category?: string;
  }): Promise<{
    markets: Array<any>;
    total: number;
  }> {
    try {
      // Belt-and-braces: status=all means "no filter" — omit the param
      // instead of sending the literal string to the backend.
      const params = filters?.status === 'all' ? { ...filters, status: undefined } : filters;
      const response = await this.client.get('/api/v1/admin/markets', { params });
      return response.data;
    } catch (error) {
      throw new Error(this.handleError(error));
    }
  }

  async getAdminLive(): Promise<{
    stats: AdminLiveStats;
    recentBets: AdminLiveBet[];
  }> {
    try {
      const response = await this.client.get('/api/v1/admin/live');
      return response.data;
    } catch (error) {
      throw new Error(this.handleError(error));
    }
  }

  // versusLive — full permanent bet feed, filterable (userId/marketId/status), paginated.
  async getAdminLiveBets(params: {
    userId?: string; marketId?: string; status?: string; limit?: number; offset?: number;
  }): Promise<{ total: number; limit: number; offset: number; bets: AdminLiveBet[] }> {
    try {
      const response = await this.client.get('/api/v1/admin/live/bets', { params });
      return response.data;
    } catch (error) {
      throw new Error(this.handleError(error));
    }
  }

  async getAdminHedges(marketId?: string): Promise<any> {
    try {
      const response = await this.client.get('/api/v1/admin/hedges', {
        params: marketId ? { marketId } : {},
      });
      return response.data;
    } catch (error) {
      throw new Error(this.handleError(error));
    }
  }

  // versusLive — users who have predicted (dropdown source).
  async getAdminLiveUsers(search?: string): Promise<{
    users: Array<{ id: string; username: string; email: string; betCount: number; totalStaked: number; lastBetAt: string }>;
  }> {
    try {
      const response = await this.client.get('/api/v1/admin/live/users', { params: search ? { search } : {} });
      return response.data;
    } catch (error) {
      throw new Error(this.handleError(error));
    }
  }

  // versusLive — one user's full history (predictions, likes, comments, badges).
  async getAdminUserHistory(userId: string): Promise<AdminLiveUserHistory> {
    try {
      const response = await this.client.get(`/api/v1/admin/live/users/${userId}/history`);
      return response.data;
    } catch (error) {
      throw new Error(this.handleError(error));
    }
  }

  // versusLive — download the filtered bet feed as CSV or JSON (authenticated blob).
  async exportAdminBets(format: 'csv' | 'json', params: { userId?: string; marketId?: string; status?: string } = {}): Promise<void> {
    try {
      const response = await this.client.get('/api/v1/admin/live/export', { params: { ...params, format }, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `versus-bets-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')}.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      throw new Error(this.handleError(error));
    }
  }

  async uploadMarketImage(formData: FormData): Promise<{ url: string }> {
    try {
      const response = await this.client.post('/api/v1/admin/markets/upload-image', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      throw new Error(this.handleError(error));
    }
  }

  async settleMarket(marketId: string): Promise<{ market: any; message: string }> {
    try {
      const response = await this.client.post(`/api/v1/admin/markets/${marketId}/settle`);
      return response.data;
    } catch (error) {
      throw new Error(this.handleError(error));
    }
  }

  async publishMarket(marketId: string): Promise<{ market: any; message: string }> {
    try {
      const response = await this.client.post(`/api/v1/admin/markets/${marketId}/publish`);
      return response.data;
    } catch (error) {
      throw new Error(this.handleError(error));
    }
  }

  async toggleMarketFeatured(marketId: string): Promise<{ market: any; message: string }> {
    try {
      const response = await this.client.post(`/api/v1/admin/markets/${marketId}/feature`);
      return response.data;
    } catch (error) {
      throw new Error(this.handleError(error));
    }
  }

  async setMarketFeaturedOrder(
    marketId: string,
    featuredOrder: number | null
  ): Promise<{ market: any; message: string }> {
    try {
      const response = await this.client.post(
        `/api/v1/admin/markets/${marketId}/feature-order`,
        { featured_order: featuredOrder }
      );
      return response.data;
    } catch (error) {
      throw new Error(this.handleError(error));
    }
  }

  async getPendingMarkets(): Promise<{ markets: any[] }> {
    try {
      console.log('API: Fetching pending markets from /api/v1/admin/markets/pending');
      const response = await this.client.get('/api/v1/admin/markets/pending');
      console.log('API: Response received:', response.data);
      console.log('API: Response type:', typeof response.data);
      console.log('API: Markets type:', typeof response.data.markets);
      console.log('API: Markets length:', response.data.markets?.length);
      console.log('API: First market raw:', response.data.markets?.[0]);
      console.log('API: First market JSON:', JSON.stringify(response.data.markets?.[0]));
      console.log('API: Response headers:', response.headers);
      return response.data;
    } catch (error) {
      console.error('API: Error fetching pending markets:', error);
      throw new Error(this.handleError(error));
    }
  }

  async approveMarket(marketId: string): Promise<{ approval: any; market: any; message: string }> {
    try {
      const response = await this.client.post(`/api/v1/admin/markets/${marketId}/approve`);
      return response.data;
    } catch (error) {
      throw new Error(this.handleError(error));
    }
  }

  async rejectMarket(marketId: string, reason?: string): Promise<{ market: any; message: string }> {
    try {
      const response = await this.client.post(`/api/v1/admin/markets/${marketId}/reject`, { reason });
      return response.data;
    } catch (error) {
      throw new Error(this.handleError(error));
    }
  }

  async getMarketApprovals(marketId: string): Promise<{ approvals: any[] }> {
    try {
      const response = await this.client.get(`/api/v1/admin/markets/${marketId}/approvals`);
      return response.data;
    } catch (error) {
      throw new Error(this.handleError(error));
    }
  }

  async markMarketsAsViewed(marketIds: string[]): Promise<{ message: string }> {
    try {
      const response = await this.client.post('/api/v1/admin/markets/mark-viewed', { marketIds });
      return response.data;
    } catch (error) {
      throw new Error(this.handleError(error));
    }
  }

  async getUnviewedPendingMarketsCount(): Promise<{ count: number }> {
    try {
      const response = await this.client.get('/api/v1/admin/markets/pending/unviewed-count');
      return response.data;
    } catch (error) {
      throw new Error(this.handleError(error));
    }
  }

  // Content reports
  async getContentReports(filters?: {
    contentType?: 'market' | 'user' | 'comment';
    status?: 'pending' | 'reviewed' | 'resolved' | 'dismissed';
    limit?: number;
    offset?: number;
  }): Promise<{
    reports: Array<{
      id: string;
      userId: string;
      contentType: string;
      contentId: string;
      reason: string;
      details: string | null;
      status: string;
      resolvedBy: string | null;
      resolvedAt: string | null;
      createdAt: string;
      reporterEmail?: string;
      reporterUsername?: string;
    }>;
    total: number;
  }> {
    try {
      const response = await this.client.get('/api/v1/admin/reports', { params: filters });
      return response.data;
    } catch (error) {
      throw new Error(this.handleError(error));
    }
  }

  async resolveContentReport(id: string, status: 'resolved' | 'dismissed'): Promise<{ report: object }> {
    try {
      const response = await this.client.patch(`/api/v1/admin/reports/${id}/resolve`, { status });
      return response.data;
    } catch (error) {
      throw new Error(this.handleError(error));
    }
  }

  // Market proposals
  async getMarketProposals(filters?: {
    status?: 'pending_review' | 'approved' | 'rejected';
    limit?: number;
    offset?: number;
  }): Promise<{
    proposals: Array<{
      id: string;
      user_id: string;
      headline: string;
      description: string;
      category: string;
      answer_type: string;
      status: string;
      created_at: string;
    }>;
    total: number;
  }> {
    try {
      const response = await this.client.get('/api/v1/admin/market-proposals', { params: filters });
      return response.data;
    } catch (error) {
      throw new Error(this.handleError(error));
    }
  }

  async approveMarketProposal(id: string): Promise<{ proposal: object }> {
    try {
      const response = await this.client.post(`/api/v1/admin/market-proposals/${id}/approve`);
      return response.data;
    } catch (error) {
      throw new Error(this.handleError(error));
    }
  }

  async rejectMarketProposal(id: string, reason?: string): Promise<{ proposal: object }> {
    try {
      const response = await this.client.post(`/api/v1/admin/market-proposals/${id}/reject`, { reason });
      return response.data;
    } catch (error) {
      throw new Error(this.handleError(error));
    }
  }

  // Versus Originals
  async getOriginalsAdmin(params?: { limit?: number; offset?: number }): Promise<{
    originals: Array<{ id: string; title: string; slug: string; type: string; publishedAt: string | null }>;
    total: number;
  }> {
    const response = await this.client.get('/api/v1/originals/admin', { params });
    return response.data;
  }

  async createOriginal(data: { title: string; slug: string; type: 'article' | 'video'; content?: string; mediaUrl?: string; publishedAt?: string }): Promise<object> {
    const response = await this.client.post('/api/v1/originals/admin', data);
    return response.data;
  }

  async updateOriginal(id: string, data: { publishedAt?: string }): Promise<object> {
    const response = await this.client.patch(`/api/v1/originals/admin/${id}`, data);
    return response.data;
  }

  // Versus Awards
  async getBadgeDefinitions(): Promise<{
    badges: Array<{
      id: string;
      name: string;
      description: string | null;
      rewardAmountCents?: number | null;
      creditsWithdrawable?: boolean;
    }>;
  }> {
    const response = await this.client.get('/api/v1/awards/definitions');
    return response.data;
  }

  async createBadge(data: {
    name: string;
    description?: string;
    icon?: string;
    criteria?: string;
    rewardAmountCents?: number | null;
    creditsWithdrawable?: boolean;
  }): Promise<object> {
    const response = await this.client.post('/api/v1/awards/admin/definitions', data);
    return response.data;
  }

  async awardBadge(userId: string, badgeId: string): Promise<{ badge: object }> {
    const response = await this.client.post('/api/v1/awards/admin/award', { userId, badgeId });
    return response.data;
  }

  // Versus Academy
  async getAcademyModules(): Promise<{ modules: Array<{ id: string; title: string; lessons: Array<{ id: string; title: string }> }> }> {
    const response = await this.client.get('/api/v1/academy/modules');
    return response.data;
  }

  async createAcademyModule(data: { title: string; description?: string; sortOrder?: number }): Promise<object> {
    const response = await this.client.post('/api/v1/academy/admin/modules', data);
    return response.data;
  }

  async createAcademyLesson(data: { moduleId: string; title: string; content?: string; sortOrder?: number }): Promise<object> {
    const response = await this.client.post('/api/v1/academy/admin/lessons', data);
    return response.data;
  }

  // System Settings
  async getAllowedRegions(): Promise<{ allowedRegions: string[] }> {
    try {
      const response = await this.client.get('/api/v1/admin/settings/allowed-regions');
      return response.data;
    } catch (error) {
      throw new Error(this.handleError(error));
    }
  }

  async setAllowedRegions(allowedRegions: string[]): Promise<{ message: string; allowedRegions: string[] }> {
    try {
      const response = await this.client.put('/api/v1/admin/settings/allowed-regions', { allowedRegions });
      return response.data;
    } catch (error) {
      throw new Error(this.handleError(error));
    }
  }

  // Withdrawals
  async getWithdrawals(status?: 'pending' | 'approved' | 'rejected' | 'cancelled'): Promise<{
    withdrawals: Array<{
      id: string;
      userId: string;
      amount: number;
      // ISO-4217 the payout leaves in. Optional: tolerates a backend that
      // predates the field (falls back to GBP in the UI).
      currency?: string;
      status: string;
      bankIban: string | null;
      bankHolder: string | null;
      // Skrill/Neteller payout target (null/absent for bank transfers).
      payoutMethod?: string | null;
      payoutEmail?: string | null;
      userEmail: string;
      username: string;
      createdAt: string;
    }>;
  }> {
    try {
      const response = await this.client.get('/api/v1/admin/withdrawals', {
        params: status ? { status } : undefined,
      });
      return response.data;
    } catch (error) {
      throw new Error(this.handleError(error));
    }
  }

  /**
   * Admin "source of funds" view: a user's wallet transactions, newest first.
   * Mirrors GET /api/v1/admin/users/:id/transactions (master-only). Optional
   * `type` filter (e.g. 'deposit' to show only the source of the balance).
   */
  async getUserTransactions(
    userId: string,
    options?: { type?: string; limit?: number; offset?: number }
  ): Promise<{
    transactions: Array<{
      id: string;
      type: string;
      amount: number;
      currency: string;
      referenceType: string | null;
      description: string | null;
      createdAt: string;
    }>;
    total: number;
  }> {
    try {
      const response = await this.client.get(`/api/v1/admin/users/${userId}/transactions`, {
        params: {
          ...(options?.type ? { type: options.type } : {}),
          ...(options?.limit != null ? { limit: options.limit } : {}),
          ...(options?.offset != null ? { offset: options.offset } : {}),
        },
      });
      return response.data;
    } catch (error) {
      throw new Error(this.handleError(error));
    }
  }

  async approveWithdrawal(id: string): Promise<{ withdrawal: { id: string; amount: number; status: string } }> {
    try {
      const response = await this.client.post(`/api/v1/admin/withdrawals/${id}/approve`);
      return response.data;
    } catch (error) {
      throw new Error(this.handleError(error));
    }
  }

  async rejectWithdrawal(id: string, reason?: string): Promise<{ withdrawal: { id: string; amount: number; status: string } }> {
    try {
      const response = await this.client.post(`/api/v1/admin/withdrawals/${id}/reject`, { reason });
      return response.data;
    } catch (error) {
      throw new Error(this.handleError(error));
    }
  }

  // Referral campaigns (Share & Earn)
  async getReferralCampaigns(): Promise<{
    campaigns: Array<{
      id: string;
      key: string;
      enabled: boolean;
      config: Record<string, unknown>;
      version: number;
      isDefault: boolean;
      publishedAt: string | null;
      createdAt: string;
      updatedAt: string;
    }>;
  }> {
    try {
      const response = await this.client.get('/api/v1/admin/referral-campaigns');
      return response.data;
    } catch (error) {
      throw new Error(this.handleError(error));
    }
  }

  async createReferralCampaign(data: { key: string; enabled?: boolean; config: Record<string, unknown> }): Promise<{
    campaign: {
      id: string;
      key: string;
      enabled: boolean;
      config: Record<string, unknown>;
      version: number;
      publishedAt: string | null;
      createdAt: string;
      updatedAt: string;
    };
  }> {
    try {
      const response = await this.client.post('/api/v1/admin/referral-campaigns', data);
      return response.data;
    } catch (error) {
      throw new Error(this.handleError(error));
    }
  }

  async updateReferralCampaign(
    id: string,
    data: { key?: string; enabled?: boolean; config?: Record<string, unknown> }
  ): Promise<{
    campaign: {
      id: string;
      key: string;
      enabled: boolean;
      config: Record<string, unknown>;
      version: number;
      publishedAt: string | null;
      createdAt: string;
      updatedAt: string;
    };
  }> {
    try {
      const response = await this.client.patch(`/api/v1/admin/referral-campaigns/${id}`, data);
      return response.data;
    } catch (error) {
      throw new Error(this.handleError(error));
    }
  }

  async publishReferralCampaign(id: string): Promise<{
    campaign: { id: string; publishedAt: string | null };
  }> {
    try {
      const response = await this.client.post(`/api/v1/admin/referral-campaigns/${id}/publish`);
      return response.data;
    } catch (error) {
      throw new Error(this.handleError(error));
    }
  }

  async validateReferralCampaign(config: Record<string, unknown>): Promise<{ valid: boolean; errors: string[] }> {
    const response = await this.client.post('/api/v1/admin/referral-campaigns/validate', { config });
    return response.data;
  }

  async previewReferralCampaign(
    config: Record<string, unknown>,
    options?: { country?: string; stakeCents?: number; oddsInt?: number }
  ): Promise<{ referrerCents: number; refereeCents: number }> {
    const response = await this.client.post('/api/v1/admin/referral-campaigns/preview', {
      config,
      ...options,
    });
    return response.data;
  }

  async dryRunGamificationRules(
    userIds: string[],
    eventType?: string
  ): Promise<{
    eventType: string;
    truncated: boolean;
    users: Array<{
      userId: string;
      eventType: string;
      diagnosticSummary: string;
      rules: Array<{
        ruleId: string;
        ruleKey: string;
        ruleType: string;
        wouldGrant: boolean;
        alreadyHasAchievement: boolean;
        countryBlocked: boolean;
        allConditionsPassed: boolean;
        conditions: Array<{ metric: string; op: string; value: number; actualValue: number; passed: boolean }>;
      }>;
    }>;
  }> {
    const response = await this.client.post('/api/v1/admin/gamification/rules/dry-run', {
      userIds,
      eventType,
    });
    return response.data;
  }

  async getReferralCampaignVersions(id: string): Promise<{
    versions: Array<{
      version: number;
      enabled: boolean;
      publishedAt: string | null;
      createdAt: string;
      config: Record<string, unknown>;
    }>;
  }> {
    const response = await this.client.get(`/api/v1/admin/referral-campaigns/${id}/versions`);
    return response.data;
  }

  async rollbackReferralCampaign(id: string, version: number): Promise<{ campaign: { id: string; version: number; publishedAt: string | null } }> {
    const response = await this.client.post(`/api/v1/admin/referral-campaigns/${id}/rollback`, { version });
    return response.data;
  }

  async setDefaultReferralCampaign(id: string): Promise<{ campaignId: string }> {
    const response = await this.client.post(`/api/v1/admin/referral-campaigns/${id}/default`);
    return response.data;
  }

  async updateReferralReviewStatus(
    id: string,
    data: { status: 'clear' | 'blocked_for_review' | 'blocked'; reason?: string }
  ): Promise<{ message: string }> {
    const response = await this.client.post(`/api/v1/admin/referrals/${id}/review-status`, data);
    return response.data;
  }

  async reverseReferralPayout(id: string, reason?: string): Promise<{ message: string; walletDebitApplied: boolean }> {
    const response = await this.client.post(`/api/v1/admin/referrals/payouts/${id}/reverse`, { reason });
    return response.data;
  }

  async blockReferralPayout(id: string, reason?: string): Promise<{ message: string; walletDebitApplied: boolean }> {
    const response = await this.client.post(`/api/v1/admin/referrals/payouts/${id}/block`, { reason });
    return response.data;
  }

  async getReferralAnalytics(): Promise<{
    summary: { clicks: number; attributions: number; conversions: number; payoutCents: number };
    campaigns: Array<{
      id: string;
      key: string;
      version: number;
      publishedAt: string | null;
      links: number;
      clicks: number;
      attributions: number;
      payoutCents: number;
    }>;
    recentPayouts: Array<{
      id: string;
      recipientRole: string;
      amountCents: number;
      status: string;
      createdAt: string;
      recipientEmail: string;
      campaignKey: string | null;
    }>;
  }> {
    const response = await this.client.get('/api/v1/admin/referrals/analytics');
    return response.data;
  }

  async getReferralAbuseReview(): Promise<{
    events: Array<{
      id: string;
      referrerId: string;
      referrerEmail: string;
      referredUserId: string;
      referredEmail: string;
      ip: string | null;
      userAgent: string | null;
      triggerOccurred: boolean;
      payoutReleasedAt: string | null;
      reviewStatus?: string | null;
      reviewReason?: string | null;
      suspicionReason: string;
    }>;
  }> {
    const response = await this.client.get('/api/v1/admin/referrals/abuse-review');
    return response.data;
  }

  /** Referred user UUID — Share & Earn “why not paid” diagnostic (master, support, finance). */
  async getReferralDiagnostics(userId: string): Promise<{
    hasAttribution: boolean;
    attributionId?: string;
    referrerId?: string;
    referrerEmail?: string;
    campaignId?: string;
    paidSettledBetCount?: number;
    triggerOccurred?: boolean;
    reviewStatus?: string;
    reviewReason?: string;
    eligibilityCheck: { eligible: boolean; reason?: string | null };
    settledBetsCount?: number;
    summary: string;
    resolution?: {
      policyResolution: 'live' | 'frozen_snapshot';
      refereeCountry: string | null;
      refereePlatform: string | null;
      referrerBrandTag: string | null;
      referrerCohortTag: string | null;
      referrerCents: number;
      refereeCents: number;
      payoutTrigger: string;
      maxBetsCount: number;
      basis: string;
      sharePercent: number;
      campaignDisabled: boolean;
    };
  }> {
    const response = await this.client.get(`/api/v1/admin/referrals/${encodeURIComponent(userId)}/diagnostics`);
    return response.data;
  }

  async getScoreModels(): Promise<{
    models: Array<{
      id: string;
      key: string;
      enabled: boolean;
      version: number;
      config: Record<string, unknown>;
      publishedAt: string | null;
      createdAt: string;
      updatedAt: string;
    }>;
  }> {
    const response = await this.client.get('/api/v1/admin/score-models');
    return response.data;
  }

  async createScoreModel(data: {
    key: string;
    enabled?: boolean;
    config: Record<string, unknown>;
  }): Promise<{ model: object }> {
    const response = await this.client.post('/api/v1/admin/score-models', data);
    return response.data;
  }

  async updateScoreModel(id: string, data: {
    enabled?: boolean;
    config?: Record<string, unknown>;
  }): Promise<{ model: object }> {
    const response = await this.client.patch(`/api/v1/admin/score-models/${id}`, data);
    return response.data;
  }

  async publishScoreModel(id: string, version?: number): Promise<{ model: object }> {
    const response = await this.client.post(`/api/v1/admin/score-models/${id}/publish`, version ? { version } : {});
    return response.data;
  }

  async getScoreModelVersions(id: string): Promise<{ versions: Array<{ version: number; config: Record<string, unknown>; publishedAt: string | null; createdAt: string }> }> {
    const response = await this.client.get(`/api/v1/admin/score-models/${id}/versions`);
    return response.data;
  }

  async rollbackScoreModel(id: string, version: number): Promise<{ model: object }> {
    const response = await this.client.post(`/api/v1/admin/score-models/${id}/rollback`, { version });
    return response.data;
  }

  async getCountryPolicies(): Promise<{
    overrides: Array<{
      id: string;
      countryCode: string;
      policyKey: string;
      config: Record<string, unknown>;
      effectiveFrom: string;
      effectiveTo: string | null;
      createdAt: string;
    }>;
  }> {
    const response = await this.client.get('/api/v1/admin/country-policies');
    return response.data;
  }

  async upsertCountryPolicy(data: {
    countryCode: string;
    policyKey: string;
    config: Record<string, unknown>;
    effectiveTo?: string | null;
  }): Promise<{ override: object }> {
    const response = await this.client.post('/api/v1/admin/country-policies', data);
    return response.data;
  }

  async getCountryPolicyVersions(countryCode: string, policyKey: string): Promise<{ versions: Array<{ version: number; config: Record<string, unknown>; effectiveFrom: string; effectiveTo: string | null; createdAt: string }> }> {
    const response = await this.client.get(`/api/v1/admin/country-policies/${encodeURIComponent(countryCode)}/${encodeURIComponent(policyKey)}/versions`);
    return response.data;
  }

  async rollbackCountryPolicy(countryCode: string, policyKey: string, version: number): Promise<{ override: object }> {
    const response = await this.client.post(`/api/v1/admin/country-policies/${encodeURIComponent(countryCode)}/${encodeURIComponent(policyKey)}/rollback`, { version });
    return response.data;
  }

  async getCreativeTemplatesAdmin(): Promise<{
    templates: Array<{
      id: string;
      key: string;
      enabled: boolean;
      channel: string;
      config: Record<string, unknown>;
      version: number;
      publishedAt: string | null;
    }>;
  }> {
    const response = await this.client.get('/api/v1/admin/creative-templates');
    return response.data;
  }

  async createCreativeTemplate(data: {
    key: string;
    enabled?: boolean;
    channel: 'instagram_story' | 'telegram' | 'feed_card' | 'web_og' | 'default';
    config: Record<string, unknown>;
  }): Promise<{ template: object }> {
    const response = await this.client.post('/api/v1/admin/creative-templates', data);
    return response.data;
  }

  async updateCreativeTemplate(
    id: string,
    data: {
      enabled?: boolean;
      channel?: 'instagram_story' | 'telegram' | 'feed_card' | 'web_og' | 'default';
      config?: Record<string, unknown>;
    }
  ): Promise<{ template: object }> {
    const response = await this.client.patch(`/api/v1/admin/creative-templates/${id}`, data);
    return response.data;
  }

  async publishCreativeTemplate(id: string): Promise<{ template: object }> {
    const response = await this.client.post(`/api/v1/admin/creative-templates/${id}/publish`);
    return response.data;
  }

  async getCreativeTemplateVersions(id: string): Promise<{ versions: Array<{ version: number; enabled: boolean; channel: string; config: Record<string, unknown>; publishedAt: string | null; createdAt: string }> }> {
    const response = await this.client.get(`/api/v1/admin/creative-templates/${id}/versions`);
    return response.data;
  }

  async rollbackCreativeTemplate(id: string, version: number): Promise<{ template: object }> {
    const response = await this.client.post(`/api/v1/admin/creative-templates/${id}/rollback`, { version });
    return response.data;
  }

  // AML Queue
  async getAmlQueue(params?: { status?: string; limit?: number; offset?: number }): Promise<{
    alerts: Array<{
      id: string;
      checkId: string;
      status: string;
      userEmail?: string;
      username?: string;
      riskLevel?: string;
      triggerType?: string;
      createdAt: string;
    }>;
    total: number;
  }> {
    try {
      const response = await this.client.get('/api/v1/admin/aml/queue', { params });
      return response.data;
    } catch (error) {
      throw new Error(this.handleError(error));
    }
  }

  async reviewAmlAlert(alertId: string, action: 'cleared' | 'escalated' | 'freeze', notes?: string): Promise<{ alert: object }> {
    try {
      const response = await this.client.post(`/api/v1/admin/aml/alerts/${alertId}/review`, { action, notes });
      return response.data;
    } catch (error) {
      throw new Error(this.handleError(error));
    }
  }

  // Support tickets
  async getTickets(filters?: {
    status?: 'open' | 'in_progress' | 'escalated' | 'resolved' | 'closed';
    assignedAdminId?: string;
    escalated?: boolean;
    limit?: number;
    offset?: number;
  }): Promise<{
    tickets: Array<{
      id: string;
      userId: string;
      subject: string;
      status: string;
      priority: string;
      assignedAdminId: string | null;
      createdAt: string;
      updatedAt: string;
      userEmail?: string;
      username?: string;
    }>;
    total: number;
  }> {
    try {
      const response = await this.client.get('/api/v1/admin/tickets', { params: filters });
      return response.data;
    } catch (error) {
      throw new Error(this.handleError(error));
    }
  }

  async getTicket(id: string): Promise<{
    ticket: {
      id: string;
      userId: string;
      subject: string;
      status: string;
      priority: string;
      assignedAdminId: string | null;
      createdAt: string;
      updatedAt: string;
      userEmail?: string;
      username?: string;
    };
    messages: Array<{
      id: string;
      ticketId: string;
      authorType: string;
      authorId: string;
      body: string;
      createdAt: string;
    }>;
  } | null> {
    try {
      const response = await this.client.get(`/api/v1/admin/tickets/${id}`);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 404) return null;
      throw new Error(this.handleError(error));
    }
  }

  async addTicketMessage(ticketId: string, body: string): Promise<{ message: { id: string; body: string; createdAt: string } }> {
    try {
      const response = await this.client.post(`/api/v1/admin/tickets/${ticketId}/messages`, { body });
      return response.data;
    } catch (error) {
      throw new Error(this.handleError(error));
    }
  }

  async updateTicket(ticketId: string, updates: { status?: string; assignedAdminId?: string | null }): Promise<{ ticket: object }> {
    try {
      const response = await this.client.patch(`/api/v1/admin/tickets/${ticketId}`, updates);
      return response.data;
    } catch (error) {
      throw new Error(this.handleError(error));
    }
  }

  // Canned responses
  async getCannedResponses(category?: string): Promise<{
    responses: Array<{
      id: string;
      name: string;
      content: string;
      category: string | null;
      createdAt: string;
    }>;
  }> {
    try {
      const response = await this.client.get('/api/v1/admin/canned-responses', { params: category ? { category } : undefined });
      return response.data;
    } catch (error) {
      throw new Error(this.handleError(error));
    }
  }

  async createCannedResponse(data: { name: string; content: string; category?: string }): Promise<{ response: object }> {
    try {
      const response = await this.client.post('/api/v1/admin/canned-responses', data);
      return response.data;
    } catch (error) {
      throw new Error(this.handleError(error));
    }
  }

  async updateCannedResponse(id: string, data: { name?: string; content?: string; category?: string }): Promise<{ response: object }> {
    try {
      const response = await this.client.patch(`/api/v1/admin/canned-responses/${id}`, data);
      return response.data;
    } catch (error) {
      throw new Error(this.handleError(error));
    }
  }

  async deleteCannedResponse(id: string): Promise<{ message: string }> {
    try {
      const response = await this.client.delete(`/api/v1/admin/canned-responses/${id}`);
      return response.data;
    } catch (error) {
      throw new Error(this.handleError(error));
    }
  }

  // Admin logs
  async getAdminLogs(lines?: number, filter?: string): Promise<{ logs: string[]; totalLines: number }> {
    try {
      const response = await this.client.get('/api/v1/admin/logs', {
        params: { lines, filter },
      });
      return response.data;
    } catch (error) {
      throw new Error(this.handleError(error));
    }
  }

  // KYC endpoints
  async getPendingKYC(): Promise<{
    submissions: Array<{
      userId: string;
      email: string;
      username: string;
      kycStatus: string;
      sumsubApplicantId: string | null;
      submittedAt: string | null;
      createdAt: string;
    }>;
    total: number;
  }> {
    try {
      const response = await this.client.get('/api/v1/admin/kyc/pending');
      return response.data;
    } catch (error) {
      throw new Error(this.handleError(error));
    }
  }

  async getKYCDetails(userId: string): Promise<{
    kycStatus: {
      status: string;
      sumsubApplicantId: string | null;
      submittedAt: string | null;
      approvedAt: string | null;
      approvedBy: string | null;
      rejectionReason: string | null;
      resubmissionRequiredReason: string | null;
    };
    images: {
      faceImageUrl: string | null;
      idFrontImageUrl: string | null;
      idBackImageUrl: string | null;
    };
    submissions: Array<{
      id: string;
      status: string;
      reviewStatus: string | null;
      adminAction: string | null;
      adminNotes: string | null;
      createdAt: string;
    }>;
  }> {
    try {
      const response = await this.client.get(`/api/v1/admin/kyc/${userId}`);
      return response.data;
    } catch (error) {
      throw new Error(this.handleError(error));
    }
  }

  async approveKYC(userId: string, notes?: string): Promise<{ message: string }> {
    try {
      const response = await this.client.post(`/api/v1/admin/kyc/${userId}/approve`, { notes });
      return response.data;
    } catch (error) {
      throw new Error(this.handleError(error));
    }
  }

  async rejectKYC(userId: string, reason: string): Promise<{ message: string }> {
    try {
      const response = await this.client.post(`/api/v1/admin/kyc/${userId}/reject`, { reason });
      return response.data;
    } catch (error) {
      throw new Error(this.handleError(error));
    }
  }

  async requestKYCDocuments(userId: string, requiredDocs: string): Promise<{ message: string }> {
    try {
      console.log('Making POST request to:', `/api/v1/admin/kyc/${userId}/request-documents`, { requiredDocs });
      const response = await this.client.post(`/api/v1/admin/kyc/${userId}/request-documents`, {
        requiredDocs,
      }, {
        method: 'POST', // Explicitly set method
        headers: {
          'Content-Type': 'application/json',
        },
      });
      console.log('Response received:', response.status, response.data);
      return response.data;
    } catch (error) {
      console.error('Error in requestKYCDocuments:', error);
      if (axios.isAxiosError(error)) {
        console.error('Axios error details:', {
          method: error.config?.method,
          url: error.config?.url,
          status: error.response?.status,
          data: error.response?.data,
        });
      }
      throw new Error(this.handleError(error));
    }
  }

  async resetKYC(userId: string): Promise<{ message: string }> {
    try {
      const response = await this.client.post(`/api/v1/admin/kyc/${userId}/reset`);
      return response.data;
    } catch (error) {
      throw new Error(this.handleError(error));
    }
  }

  /**
   * True when the failure is a Fastify route-miss (endpoint not deployed yet),
   * as opposed to a 404 for a missing resource. Fastify's default not-found
   * payload message starts with "Route GET:/..." / "Route POST:/...".
   */
  private isRouteMissing(error: unknown): boolean {
    if (!axios.isAxiosError(error) || error.response?.status !== 404) return false;
    const data = error.response?.data as { message?: string } | undefined;
    return typeof data?.message === 'string' && data.message.startsWith('Route ');
  }

  async getManualReviewKYC(): Promise<{
    submissions: Array<{
      userId: string;
      email: string;
      username: string;
      kycStatus: string;
      sumsubApplicantId: string | null;
      rejectionReason: string | null;
      twoPlusTwoMatches: number | null;
      submittedAt: string | null;
      createdAt: string;
    }>;
    total: number;
  }> {
    try {
      const response = await this.client.get('/api/v1/admin/kyc/manual-review');
      return response.data;
    } catch (error) {
      throw new Error(this.handleError(error));
    }
  }

  // Suspended queue — same list shape as manual-review. The endpoint ships via
  // a parallel backend PR; a route-miss 404 is surfaced with a marker message
  // so the UI can show a "not deployed yet" state instead of a raw error.
  async getSuspendedKYC(): Promise<{
    submissions: Array<{
      userId: string;
      email: string;
      username: string;
      kycStatus: string;
      sumsubApplicantId: string | null;
      rejectionReason: string | null;
      twoPlusTwoMatches: number | null;
      submittedAt: string | null;
      createdAt: string;
    }>;
    total: number;
  }> {
    try {
      const response = await this.client.get('/api/v1/admin/kyc/suspended');
      return response.data;
    } catch (error) {
      if (this.isRouteMissing(error)) {
        throw new Error('SUSPENDED_QUEUE_UNAVAILABLE');
      }
      throw new Error(this.handleError(error));
    }
  }

  async suspendKYC(userId: string, reason: string): Promise<{ message: string }> {
    try {
      const response = await this.client.post(`/api/v1/admin/kyc/${userId}/suspend`, { reason });
      return response.data;
    } catch (error) {
      if (this.isRouteMissing(error)) {
        throw new Error('Suspend endpoint is not deployed yet — backend rollout pending.');
      }
      throw new Error(this.handleError(error));
    }
  }

  async unsuspendKYC(userId: string, reason?: string): Promise<{ message: string }> {
    try {
      const response = await this.client.post(
        `/api/v1/admin/kyc/${userId}/unsuspend`,
        reason && reason.trim() ? { reason: reason.trim() } : {}
      );
      return response.data;
    } catch (error) {
      if (this.isRouteMissing(error)) {
        throw new Error('Unsuspend endpoint is not deployed yet — backend rollout pending.');
      }
      throw new Error(this.handleError(error));
    }
  }

  /**
   * Fetch a KYC document image with the admin bearer token and return an
   * object URL. The image endpoints require the Authorization header, so a
   * plain <img src> cannot load them. Caller must revoke the URL when done.
   */
  async fetchKYCImageBlob(relativeUrl: string): Promise<string> {
    try {
      const response = await this.client.get(relativeUrl, { responseType: 'blob' });
      return URL.createObjectURL(response.data);
    } catch (error) {
      throw new Error(this.handleError(error));
    }
  }

  // =========================================================================
  // RISK MONITORING
  // =========================================================================

  async getRiskOverview(): Promise<{
    stats: {
      totalPoolAcrossMarkets: number;
      totalLiabilityAcrossMarkets: number;
      liveMarketCount: number;
      activeBetCount: number;
      activeReserveCount: number;
      pendingSwitchOfferCount: number;
    };
  }> {
    try {
      const response = await this.client.get('/api/v1/admin/risk/overview');
      return response.data;
    } catch (error) {
      throw new Error(this.handleError(error));
    }
  }

  async getRiskMarkets(): Promise<{
    markets: Array<{
      id: string;
      headline: string;
      status: string;
      totalPool: number;
      totalLiability: number;
      maxPotentialLoss: number;
      marginEstimate: number;
      outcomeCount: number;
      activeBetCount: number;
      healthStatus: 'healthy' | 'warning' | 'critical';
      issues: string[];
      warnings: string[];
    }>;
  }> {
    try {
      const response = await this.client.get('/api/v1/admin/risk/markets');
      return response.data;
    } catch (error) {
      throw new Error(this.handleError(error));
    }
  }

  async getRiskMarketDetail(marketId: string): Promise<{
    overview: {
      id: string;
      headline: string;
      status: string;
      totalPool: number;
      totalLiability: number;
      maxPotentialLoss: number;
      marginEstimate: number;
      outcomeCount: number;
      activeBetCount: number;
      healthStatus: 'healthy' | 'warning' | 'critical';
      issues: string[];
      warnings: string[];
    };
    outcomes: Array<{
      id: string;
      name: string;
      totalStake: number;
      liability: number;
      maxLiability: number;
      utilizationPercent: number;
      currentOdds: number;
      impliedProbability: number;
      activeBetCount: number;
      status: string;
    }>;
    pnlProjections: Array<{
      outcomeId: string;
      outcomeName: string;
      impliedProbability: number;
      potentialPnL: number;
      expectedPnL: number;
    }>;
  }> {
    try {
      const response = await this.client.get(`/api/v1/admin/risk/markets/${marketId}`);
      return response.data;
    } catch (error) {
      throw new Error(this.handleError(error));
    }
  }

  async getRiskActivity(options?: { marketId?: string; limit?: number }): Promise<{
    activity: Array<{
      id: string;
      type: string;
      userId: string;
      marketId: string;
      marketHeadline: string;
      amount: number;
      description: string;
      createdAt: string;
    }>;
  }> {
    try {
      const response = await this.client.get('/api/v1/admin/risk/activity', { params: options });
      return response.data;
    } catch (error) {
      throw new Error(this.handleError(error));
    }
  }

  async getTopExposure(limit?: number): Promise<{
    users: Array<{
      userId: string;
      username: string;
      totalStaked: number;
      potentialPayout: number;
      activeBetCount: number;
    }>;
  }> {
    try {
      const response = await this.client.get('/api/v1/admin/risk/top-exposure', { params: { limit } });
      return response.data;
    } catch (error) {
      throw new Error(this.handleError(error));
    }
  }

  async getRiskConfig(): Promise<{
    config: {
      id: string;
      targetMargin: number;
      lambdaMix: number;
      beta: number;
      liquiditySeed: number;
      minBet: number;
      maxSharePerBet: number;
      impactAlpha: number;
      impactK: number;
      impactR0: number;
      impactRMax: number;
    } | null;
  }> {
    try {
      const response = await this.client.get('/api/v1/admin/risk/config');
      return response.data;
    } catch (error) {
      throw new Error(this.handleError(error));
    }
  }

  async updateRiskConfig(config: Partial<{
    targetMargin: number;
    lambdaMix: number;
    beta: number;
    liquiditySeed: number;
    minBet: number;
    maxSharePerBet: number;
    impactAlpha: number;
    impactK: number;
    impactR0: number;
    impactRMax: number;
  }>): Promise<{ config: any }> {
    try {
      const response = await this.client.put('/api/v1/admin/risk/config', config);
      return response.data;
    } catch (error) {
      throw new Error(this.handleError(error));
    }
  }

  async settleMarketWithOutcome(marketId: string, winningOutcomeId: string): Promise<{
    message: string;
    totalPayout: number;
    winnerCount: number;
  }> {
    try {
      const response = await this.client.post(`/api/v1/admin/risk/markets/${marketId}/settle`, {
        winningOutcomeId,
      });
      return response.data;
    } catch (error) {
      throw new Error(this.handleError(error));
    }
  }

  // Analytics
  async getRevenueAnalytics(period: '7d' | '30d' = '30d'): Promise<{
    data: Array<{ date: string; revenue: number; volume: number; betCount: number }>;
  }> {
    const response = await this.client.get('/api/v1/admin/analytics/revenue', { params: { period } });
    return response.data;
  }

  async getUserAnalytics(period: '7d' | '30d' = '30d'): Promise<{
    data: Array<{ date: string; newUsers: number; totalUsers: number }>;
  }> {
    const response = await this.client.get('/api/v1/admin/analytics/users', { params: { period } });
    return response.data;
  }

  // Challenges
  async getChallenges(params?: { status?: string; limit?: number; offset?: number }): Promise<{
    challenges: Array<{
      id: string;
      userId: string;
      username: string;
      userEmail: string;
      title: string;
      description: string | null;
      status: string;
      reviewedBy: string | null;
      reviewedAt: string | null;
      rejectionReason: string | null;
      createdAt: string;
    }>;
    total: number;
  }> {
    const response = await this.client.get('/api/v1/admin/challenges', { params });
    return response.data;
  }

  async createChallenge(data: { title: string; description?: string; userId: string }): Promise<{ challenge: { id: string; userId: string; title: string; description: string | null; status: string; createdAt: string } }> {
    const response = await this.client.post('/api/v1/admin/challenges', data);
    return response.data;
  }

  async approveChallenge(id: string): Promise<{ message: string }> {
    const response = await this.client.post(`/api/v1/admin/challenges/${id}/approve`);
    return response.data;
  }

  async rejectChallenge(id: string, reason?: string): Promise<{ message: string }> {
    const response = await this.client.post(`/api/v1/admin/challenges/${id}/reject`, { reason });
    return response.data;
  }

  // Push Notifications (Agent 94)
  async createPushBroadcast(payload: {
    title: string;
    body: string;
    deeplink?: string;
    /** Optional rich-push image — sent as snake_case image_url (additive; older backends ignore it). */
    imageUrl?: string;
    cohort:
      | { type: 'all' | 'verified' | 'pending_kyc' | 'inactive_7d' | 'high_value' }
      | { type: 'user_ids'; user_ids: string[] };
  }): Promise<{ id: string; status: string; recipient_count: number; created_at: string }> {
    const { imageUrl, ...rest } = payload;
    const response = await this.client.post('/api/v1/admin/notifications/push', {
      ...rest,
      ...(imageUrl ? { image_url: imageUrl } : {}),
    });
    return response.data;
  }

  async listPushBroadcasts(params?: { limit?: number; offset?: number }): Promise<{
    broadcasts: Array<{
      id: string;
      title: string;
      body: string;
      deeplink: string | null;
      imageUrl: string | null;
      cohort: string;
      status: string;
      recipientCount: number;
      sentCount: number;
      errorMessage: string | null;
      createdAt: string;
      sentAt: string | null;
      adminEmail: string | null;
    }>;
    total: number;
  }> {
    const response = await this.client.get('/api/v1/admin/notifications/push', { params });
    return response.data;
  }

  async getAiPushSettings(): Promise<{
    ai_push_frequency_enabled: boolean;
    ai_push_time_of_day: 'morning' | 'afternoon' | 'evening';
    ai_push_triggered_inactive: boolean;
    ai_push_triggered_settled: boolean;
    ai_push_max_per_day: 1 | 2 | 3 | 5;
  }> {
    const response = await this.client.get('/api/v1/admin/settings/ai-push');
    return response.data;
  }

  async updateAiPushSettings(payload: {
    ai_push_frequency_enabled?: boolean;
    ai_push_time_of_day?: 'morning' | 'afternoon' | 'evening';
    ai_push_triggered_inactive?: boolean;
    ai_push_triggered_settled?: boolean;
    ai_push_max_per_day?: 1 | 2 | 3 | 5;
  }): Promise<{ message: string }> {
    const response = await this.client.put('/api/v1/admin/settings/ai-push', payload);
    return response.data;
  }

  // ===========================================================================
  // FEE CREDIT + VOUCHER + AFFILIATE — backend PR1 (feat/fee-credit-system)
  // + PR2 (feat/bet-platform-fee). Money is BIGINT cents on the wire; we ship
  // strings to keep precision, and the UI converts to euros for display.
  // ===========================================================================

  // ---- Vouchers ----
  async listVouchers(params: {
    partnerId?: string;
    status?: 'active' | 'paused' | 'expired' | 'revoked' | 'exhausted';
    label?: string;                              // PR3: case-insensitive substring on internal_label
    currency?: 'EUR' | 'USD' | 'GBP';            // PR3: exact match
    voucherType?: 'fee_credit' | 'wallet_credit'; // PR4: filter by type
    limit?: number;
    offset?: number;
  } = {}): Promise<{
    vouchers: Array<{
      id: string;
      code: string;
      partnerId: string | null;
      feeCreditAmount: number;
      feeCreditAmountCents: string;
      currency: string;
      maxRedemptions: number;
      redemptionCount: number;
      perUserLimit: number;
      feeCreditExpiresInDays: number;
      voucherValidUntil: string;
      requiresKyc: boolean;
      minAccountAgeDays: number;
      status: string;
      createdBy: string;
      internalLabel: string | null;              // PR3
      voucherType: 'fee_credit' | 'wallet_credit'; // PR4
      creditsWithdrawable: boolean;              // PR4
      createdAt: string;
    }>;
    total: number;
  }> {
    const response = await this.client.get('/api/v1/admin/vouchers', { params });
    return response.data;
  }

  async generateVouchers(payload: {
    partnerId?: string | null;
    campaignId?: string | null;
    feeCreditAmountCents: string;
    quantity: number;
    voucherValidUntil: string;
    feeCreditExpiresInDays: number;
    perUserLimit?: number;
    maxRedemptionsPerCode?: number;
    requiresKyc?: boolean;
    minAccountAgeDays?: number;
    currency?: 'EUR' | 'USD' | 'GBP';            // PR3: defaults to EUR server-side
    internalLabel?: string;                      // PR3: free-form admin tag, ≤ 200 chars
    metadata?: Record<string, unknown>;
  }): Promise<{ codes: string[]; voucherIds: string[] }> {
    const response = await this.client.post('/api/v1/admin/vouchers/generate', payload);
    return response.data;
  }

  async revokeVoucher(voucherId: string, reason: string): Promise<{ ok: true }> {
    const response = await this.client.post(`/api/v1/admin/vouchers/${voucherId}/revoke`, { reason });
    return response.data;
  }

  // ---- Affiliate partners ----
  async listAffiliatePartners(params: {
    status?: 'active' | 'paused' | 'terminated';
    limit?: number;
    offset?: number;
  } = {}): Promise<{
    partners: Array<{
      id: string;
      name: string;
      slug: string;
      codePrefix: string;
      email: string;
      status: 'active' | 'paused' | 'terminated';
      hasApiKey: boolean;
      apiKeyLastRotatedAt: string | null;
      webhookUrl: string | null;
      notes: string | null;
      createdAt: string;
      updatedAt: string;
    }>;
    total: number;
  }> {
    const response = await this.client.get('/api/v1/admin/affiliate-partners', { params });
    return response.data;
  }

  async createAffiliatePartner(payload: {
    name: string;
    slug: string;
    codePrefix: string;
    email: string;
    webhookUrl?: string;
    notes?: string;
  }): Promise<{ partner: { id: string; name: string; slug: string; codePrefix: string; status: string; hasApiKey: boolean } }> {
    const response = await this.client.post('/api/v1/admin/affiliate-partners', payload);
    return response.data;
  }

  async getAffiliatePartner(id: string): Promise<{ partner: {
    id: string;
    name: string;
    slug: string;
    codePrefix: string;
    email: string;
    status: 'active' | 'paused' | 'terminated';
    hasApiKey: boolean;
    apiKeyLastRotatedAt: string | null;
    webhookUrl: string | null;
    notes: string | null;
    createdAt: string;
    updatedAt: string;
  } }> {
    const response = await this.client.get(`/api/v1/admin/affiliate-partners/${id}`);
    return response.data;
  }

  /**
   * Returns the plaintext API key ONCE. The bcrypt hash is what gets stored;
   * the UI must show the key to the partner immediately and never persist it.
   */
  async rotatePartnerApiKey(id: string): Promise<{ apiKey: string; warning: string }> {
    const response = await this.client.post(`/api/v1/admin/affiliate-partners/${id}/api-key/rotate`);
    return response.data;
  }

  async pauseAffiliatePartner(id: string): Promise<{ ok: true }> {
    const response = await this.client.post(`/api/v1/admin/affiliate-partners/${id}/pause`);
    return response.data;
  }

  async activateAffiliatePartner(id: string): Promise<{ ok: true }> {
    const response = await this.client.post(`/api/v1/admin/affiliate-partners/${id}/activate`);
    return response.data;
  }

  async terminateAffiliatePartner(id: string): Promise<{ ok: true }> {
    const response = await this.client.post(`/api/v1/admin/affiliate-partners/${id}/terminate`);
    return response.data;
  }

  async getAffiliatePartnerStats(id: string, range: { from?: string; to?: string } = {}): Promise<{
    partnerId: string;
    vouchersIssued: number;
    redemptions: number;
    uniqueRedeemers: number;
    totalFeeCreditGranted: number;
    totalFeeCreditGrantedCents: string;
  }> {
    const response = await this.client.get(`/api/v1/admin/affiliate-partners/${id}/stats`, { params: range });
    return response.data;
  }

  // ---- Platform fee config (PR2) ----
  async getPlatformFeeConfig(): Promise<{
    basisPoints: number;
    minFeeCents: number;
    maxFeeCents: number | null;
    enabled: boolean;
    updatedBy: string;
    reason: string | null;
    updatedAt: string;
  }> {
    const response = await this.client.get('/api/v1/admin/platform-fee-config');
    return response.data;
  }

  async updatePlatformFeeConfig(payload: {
    basisPoints?: number;
    minFeeCents?: number;
    maxFeeCents?: number | null;
    enabled?: boolean;
    reason: string;
  }): Promise<{
    basisPoints: number;
    minFeeCents: number;
    maxFeeCents: number | null;
    enabled: boolean;
    updatedBy: string;
    reason: string | null;
    updatedAt: string;
  }> {
    const response = await this.client.put('/api/v1/admin/platform-fee-config', payload);
    return response.data;
  }

  async getPlatformFeeConfigHistory(params: { limit?: number; offset?: number } = {}): Promise<{
    rows: Array<{
      id: number;
      basisPoints: number;
      minFeeCents: number;
      maxFeeCents: number | null;
      enabled: boolean;
      changedBy: string;
      changedAt: string;
      reason: string | null;
    }>;
    total: number;
  }> {
    const response = await this.client.get('/api/v1/admin/platform-fee-config/history', { params });
    return response.data;
  }

  // ===========================================================================
  // WALLET-CREDIT VOUCHERS — versusapp PR4. The generate route is gated by
  // a Dubai-time-code (HHMM in UTC+4) rotating every minute; callers pass it
  // via the `timeCode` opts arg, which becomes the X-Versus-Time-Code header.
  // ===========================================================================

  async generateWalletCreditVouchers(
    payload: {
      feeCreditAmountCents: string;
      quantity: number;
      voucherValidUntil: string;
      feeCreditExpiresInDays: number;
      perUserLimit?: number;
      maxRedemptionsPerCode?: number;
      requiresKyc?: boolean;
      minAccountAgeDays?: number;
      currency?: 'EUR' | 'USD' | 'GBP';
      internalLabel?: string;
      creditsWithdrawable?: boolean;
      reason: string;                              // required ≥ 20 chars
      partnerId?: string | null;
    },
    opts: { timeCode: string }
  ): Promise<{
    codes: string[];
    voucherIds: string[];
    auditIds: string[];
    totalAmountCents: string;
    currency: string;
    creditsWithdrawable: boolean;
  }> {
    const response = await this.client.post(
      '/api/v1/admin/wallet-credit-vouchers/generate',
      payload,
      { headers: { 'X-Versus-Time-Code': opts.timeCode } }
    );
    return response.data;
  }

  async listWalletCreditVoucherAudit(params: {
    adminId?: string;
    limit?: number;
    offset?: number;
  } = {}): Promise<{
    rows: Array<{
      id: string;
      voucherId: string;
      voucherCode: string;
      voucherStatus: string;
      adminId: string;
      adminEmail: string;
      ipAddress: string | null;
      userAgent: string | null;
      amountCents: string;
      currency: string;
      creditsWithdrawable: boolean;
      reason: string;
      createdAt: string;
    }>;
    total: number;
  }> {
    const response = await this.client.get('/api/v1/admin/wallet-credit-vouchers/audit', { params });
    return response.data;
  }

  // ---------------------------------------------------------------------------
  // Versus AI chat (BE-AI-PROXY). Backend in versusapp PR #87:
  //   GET    /api/v1/admin/ai/settings        — read singleton config
  //   PATCH  /api/v1/admin/ai/settings        — update toggle / model / caps
  //   GET    /api/v1/admin/ai/usage?days=&limit= — spend rollup + top users
  // ---------------------------------------------------------------------------

  async getAIChatSettings(): Promise<{
    enabled: boolean;
    provider: 'deepseek' | 'openai' | 'anthropic';
    model: string;
    dailyCostCapCents: number;
    perUserMessagesPerHour: number;
    maxTokensPerRequest: number;
    updatedAt: string;
    updatedByAdminId: string | null;
    keyConfigured: boolean;
  }> {
    const response = await this.client.get('/api/v1/admin/ai/settings');
    return response.data;
  }

  async updateAIChatSettings(patch: {
    enabled?: boolean;
    provider?: 'deepseek' | 'openai' | 'anthropic';
    model?: string;
    dailyCostCapCents?: number;
    perUserMessagesPerHour?: number;
    maxTokensPerRequest?: number;
  }): Promise<{
    enabled: boolean;
    provider: 'deepseek' | 'openai' | 'anthropic';
    model: string;
    dailyCostCapCents: number;
    perUserMessagesPerHour: number;
    maxTokensPerRequest: number;
    updatedAt: string;
    updatedByAdminId: string | null;
    keyConfigured: boolean;
  }> {
    const response = await this.client.patch('/api/v1/admin/ai/settings', patch);
    return response.data;
  }

  async getAIChatUsage(days = 7, limit = 20): Promise<{
    windowDays: number;
    totals: {
      total_cost_cents: number;
      total_tokens_in: number;
      total_tokens_out: number;
      total_requests: number;
    };
    dailySpendCents: number;
    topUsers: Array<{
      id: string;
      username: string;
      email: string;
      cost_cents: number;
      tokens_in: number;
      tokens_out: number;
      requests: number;
    }>;
  }> {
    const response = await this.client.get('/api/v1/admin/ai/usage', {
      params: { days, limit },
    });
    return response.data;
  }

  // ---------------------------------------------------------------------------
  // Pulse source aggregation. Backend in versusapp routes/adminPulse.ts:
  //   POST   /api/v1/admin/bets/:id/sources              — paste tweet/reddit URL → ingest
  //   DELETE /api/v1/admin/bets/:id/sources/:sourceId    — soft-delete
  // The X bearer never reaches the browser — it lives in AWS Secrets Manager
  // (versus/staging/pulse-x-api-bearer) and is read by ECS at task start.
  // ---------------------------------------------------------------------------

  async attachPulseSource(
    betId: string,
    url: string,
    cacheTtlHours?: number,
  ): Promise<{
    ingested: {
      provider: 'X' | 'REDDIT';
      external_id: string;
      external_url: string;
      author_handle: string;
      status: 'new' | 'updated';
    };
  }> {
    const body: { url: string; cache_ttl_hours?: number } = { url };
    if (cacheTtlHours !== undefined) body.cache_ttl_hours = cacheTtlHours;
    const response = await this.client.post(`/api/v1/admin/bets/${betId}/sources`, body);
    return response.data;
  }

  // ---------------------------------------------------------------------------
  // Versus Expert — Tier-2 AI support agent (admin role: master|support).
  // W1 shadow mode (backend in versusapp PR #453):
  //   GET  /api/v1/admin/expert-chat/shadow-runs?limit=&cursor=      — list runs
  //   GET  /api/v1/admin/expert-chat/shadow-runs/:id                 — full run + transcript
  //   POST /api/v1/admin/expert-chat/shadow-runs                     — trigger run (can take MINUTES)
  // W2 action approval queue (contract final, backend follows in a separate PR):
  //   GET  /api/v1/admin/expert-chat/actions?status=&limit=&cursor=  — proposed actions
  //   POST /api/v1/admin/expert-chat/actions/:id/approve             — approve + execute (sync)
  //   POST /api/v1/admin/expert-chat/actions/:id/decline             — decline with reason
  // Errors are rethrown with `.status` attached so the page can map 404/503 to
  // friendly states (feature detection for W2, "expert disabled" messaging).
  // ---------------------------------------------------------------------------

  private expertError(error: unknown): Error & { status?: number } {
    const err = new Error(this.handleError(error)) as Error & { status?: number };
    if (axios.isAxiosError(error)) err.status = error.response?.status;
    return err;
  }

  async getExpertShadowRuns(
    params: { limit?: number; cursor?: string } = {},
  ): Promise<{ runs: ExpertShadowRunSummary[]; nextCursor: string | null }> {
    try {
      const response = await this.client.get('/api/v1/admin/expert-chat/shadow-runs', { params });
      return response.data;
    } catch (error) {
      throw this.expertError(error);
    }
  }

  async getExpertShadowRun(id: string): Promise<ExpertShadowRun> {
    try {
      const response = await this.client.get(`/api/v1/admin/expert-chat/shadow-runs/${id}`);
      return response.data;
    } catch (error) {
      throw this.expertError(error);
    }
  }

  // Triggers a live agentic run — the agent researches with read-tools and the
  // request only returns once the whole session is done. This can take MINUTES,
  // so `timeout: 0` disables the 30s instance default. Never add a timeout here.
  async createExpertShadowRun(body: {
    userId: string;
    question: string;
    conversationId?: string;
  }): Promise<ExpertShadowRun> {
    try {
      const response = await this.client.post('/api/v1/admin/expert-chat/shadow-runs', body, {
        timeout: 0,
      });
      return response.data;
    } catch (error) {
      throw this.expertError(error);
    }
  }

  async getExpertActions(
    params: { status?: string; limit?: number; cursor?: string } = {},
  ): Promise<{ actions: ExpertAction[]; nextCursor: string | null }> {
    try {
      const response = await this.client.get('/api/v1/admin/expert-chat/actions', { params });
      return response.data;
    } catch (error) {
      throw this.expertError(error);
    }
  }

  // Backend executes the action synchronously on approve — allow it more headroom
  // than the 30s default (e.g. refunds touching the payment layer).
  async approveExpertAction(id: string): Promise<{ action: ExpertAction }> {
    try {
      const response = await this.client.post(
        `/api/v1/admin/expert-chat/actions/${id}/approve`,
        {},
        { timeout: 120000 },
      );
      return response.data;
    } catch (error) {
      throw this.expertError(error);
    }
  }

  async declineExpertAction(id: string, reason: string): Promise<{ action: ExpertAction }> {
    try {
      const response = await this.client.post(`/api/v1/admin/expert-chat/actions/${id}/decline`, {
        reason,
      });
      return response.data;
    } catch (error) {
      throw this.expertError(error);
    }
  }

  async requestExpertActionInfo(
    id: string,
    instruction: string,
  ): Promise<{ action: ExpertAction }> {
    try {
      const response = await this.client.post(
        `/api/v1/admin/expert-chat/actions/${id}/request-info`,
        { instruction },
      );
      return response.data;
    } catch (error) {
      throw this.expertError(error);
    }
  }

  /**
   * "Ask the AI about this case" — read-only Q&A against one action's context
   * (versusapp PR #528). Never mutates. The LLM call can take several seconds,
   * so allow more headroom than the 30s default. `history` keeps the per-card
   * thread so follow-ups stay in context.
   */
  async askExpertAboutAction(
    id: string,
    question: string,
    history?: ExpertChatTurn[],
  ): Promise<{ answer: string; model: string }> {
    try {
      const response = await this.client.post(
        `/api/v1/admin/expert-chat/actions/${id}/ask`,
        { question, ...(history && history.length ? { history } : {}) },
        { timeout: 120000 },
      );
      return response.data;
    } catch (error) {
      throw this.expertError(error);
    }
  }

  /**
   * Aggregated support context for one user (founder 2026-07-04): profile,
   * wallet, KYC state, badges, recent predictions/ledger/payments, tickets
   * and expert-action history — the queue card's user drawer.
   */
  async getExpertUserContext(userId: string): Promise<ExpertUserContext> {
    try {
      const response = await this.client.get(
        `/api/v1/admin/expert-chat/users/${userId}/context`,
      );
      return response.data;
    } catch (error) {
      throw this.expertError(error);
    }
  }

  // ── Live Admin Control (remote app config · ordering · promo uploads) ────
  // Endpoint paths live in LIVE_CONTROL_ENDPOINTS (config/api.ts) — the
  // backend ships in parallel, so a contract drift is a one-line fix there.

  /** GET app-config → { version, config } (config normalized, never throws on shape). */
  async getAppConfig(): Promise<{ version: number; config: AppConfig }> {
    try {
      const response = await this.client.get(LIVE_CONTROL_ENDPOINTS.appConfig);
      const data = response.data ?? {};
      return {
        version: typeof data.version === 'number' ? data.version : 0,
        config: normalizeAppConfig(data.config),
      };
    } catch (error) {
      throw new Error(this.handleError(error));
    }
  }

  /** PUT app-config (body = config JSON) → saves, version++. */
  async saveAppConfig(config: AppConfig): Promise<{ version: number; config: AppConfig }> {
    try {
      // The editors keep "no date" as '' but the backend schema only accepts a
      // date-time string or null — '' fails ajv's format check and the whole
      // publish 400s. Convert on the wire, keep '' in local state.
      const payload = {
        ...config,
        promo: {
          ...config.promo,
          startsAt: config.promo.startsAt ? config.promo.startsAt : null,
          endsAt: config.promo.endsAt ? config.promo.endsAt : null,
        },
      };
      const response = await this.client.put(LIVE_CONTROL_ENDPOINTS.appConfig, payload);
      const data = response.data ?? {};
      return {
        version: typeof data.version === 'number' ? data.version : 0,
        config: normalizeAppConfig(data.config ?? config),
      };
    } catch (error) {
      throw new Error(this.handleError(error));
    }
  }

  /** Bulk market ordering: PUT { items: [{ id, displayOrder, pinned, featured, dashboardRank? }] }.
   *  Returns { updated, missing } — missing ids mean the panel's list is stale. */
  async setMarketsOrdering(items: MarketOrderingItem[]): Promise<{ updated: number; missing: string[] }> {
    try {
      const response = await this.client.put(LIVE_CONTROL_ENDPOINTS.marketsOrdering, { items });
      const data = response.data ?? {};
      return {
        updated: typeof data.updated === 'number' ? data.updated : 0,
        missing: Array.isArray(data.missing) ? data.missing : [],
      };
    } catch (error) {
      throw new Error(this.handleError(error));
    }
  }

  /** Upload a promo/push image (multipart field "file", same as market upload) → { url }. */
  async uploadPromoAsset(file: File): Promise<{ url: string }> {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await this.client.post(LIVE_CONTROL_ENDPOINTS.promoUpload, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    } catch (error) {
      throw new Error(this.handleError(error));
    }
  }

  /**
   * Vouchers for the promo composer's picker. GET /api/v1/admin/vouchers →
   * { vouchers:[{ id, code, feeCreditAmount (in currency units), currency,
   * voucherValidUntil, status, ... }], total }. Parsed defensively so a
   * backend field drift never throws — missing fields become sensible blanks.
   */
  async getVouchers(params?: { status?: string; limit?: number }): Promise<{ vouchers: AdminVoucher[] }> {
    try {
      const response = await this.client.get('/api/v1/admin/vouchers', {
        params: { status: params?.status, limit: params?.limit ?? 200 },
      });
      const raw = response.data as { vouchers?: unknown } | undefined;
      const list = Array.isArray(raw?.vouchers) ? raw!.vouchers : [];
      const vouchers: AdminVoucher[] = list
        .map((v): AdminVoucher | null => {
          if (!v || typeof v !== 'object') return null;
          const r = v as Record<string, unknown>;
          const code = typeof r.code === 'string' ? r.code : '';
          if (!code) return null;
          const amount =
            typeof r.feeCreditAmount === 'number'
              ? r.feeCreditAmount
              : typeof r.amount === 'number'
              ? (r.amount as number)
              : null;
          return {
            id: typeof r.id === 'string' ? r.id : code,
            code,
            amount,
            currency: typeof r.currency === 'string' ? r.currency : '',
            validUntil: typeof r.voucherValidUntil === 'string' ? r.voucherValidUntil : null,
            status: typeof r.status === 'string' ? r.status : '',
          };
        })
        .filter((v): v is AdminVoucher => v !== null);
      return { vouchers };
    } catch (error) {
      throw new Error(this.handleError(error));
    }
  }
}

/** A voucher row as the promo composer needs it (defensively normalized). */
export interface AdminVoucher {
  id: string;
  code: string;
  /** In currency units (already divided by 100 by the backend), or null. */
  amount: number | null;
  currency: string;
  /** ISO-8601 or null. */
  validUntil: string | null;
  status: string;
}

// ── Versus Expert types (shadow runs W1 + action queue W2) ──────────────────

export interface ExpertRunUsage {
  input_tokens?: number;
  output_tokens?: number;
  cache_read_input_tokens?: number;
  cache_creation_input_tokens?: number;
  api_turns?: number;
}

export interface ExpertShadowRunSummary {
  id: string;
  userId: string;
  trigger: string;
  question: string;
  status: 'ok' | 'refusal' | 'error';
  model: string;
  latencyMs: number;
  usage: ExpertRunUsage | null;
  error: string | null;
  createdAt: string;
}

/** Anthropic-style message content block — kept loose, rendered defensively. */
export interface ExpertContentBlock {
  type?: string;
  text?: string;
  name?: string;
  input?: any;
  content?: any;
  is_error?: boolean;
  [key: string]: any;
}

export interface ExpertTranscriptMessage {
  role: 'user' | 'assistant' | string;
  content: string | ExpertContentBlock[];
}

export interface ExpertShadowRun extends ExpertShadowRunSummary {
  sourceConversationId: string | null;
  transcript: ExpertTranscriptMessage[];
  answer: string | null;
  proposedActions: any;
}

export type ExpertActionType =
  | 'refund'
  | 'grant_voucher'
  | 'update_kyc_name'
  | 'close_account'
  | 'self_exclude'
  | 'flag_market_for_review'
  /** An escalate_to_human case parked as a visible queue item (approve = mark handled). */
  | 'manual_review';

export type ExpertActionStatus =
  | 'proposed'
  | 'auto_approved'
  | 'pending_review'
  | 'info_requested'
  | 'approved'
  | 'declined'
  | 'executed'
  | 'failed';

/** At-a-glance safety signal from the Fable reviewer (versusapp PR #528). */
export type ExpertActionRating = 'safe_to_approve' | 'review_recommended' | 'high_risk';

/** W2.5 independent compliance review (null until reviewed / not applicable). */
export interface ExpertActionReview {
  recommendation: 'approve' | 'decline' | 'need_info';
  reasoning: string;
  riskFlags: string[];
  infoNeeded: string | null;
  model: string | null;
  reviewedAt: string | null;
  /** Coloured at-a-glance safety chip (PR #528). */
  rating?: ExpertActionRating | string | null;
  /** One-line case summary: what the user wants + the exact prepared change (PR #528). */
  summary?: string | null;
}

/** One turn in the per-card "Ask the AI about this case" thread. */
export interface ExpertChatTurn {
  role: 'agent' | 'assistant';
  content: string;
}

export interface ExpertAction {
  id: string;
  sessionId: string;
  userId: string;
  /** Joined user identity (list endpoint only). */
  user?: { username: string | null; email: string | null } | null;
  type: ExpertActionType | string;
  payload: any;
  recommendation: string | null;
  evidence: any;
  status: ExpertActionStatus | string;
  decidedBy: string | null;
  decidedAt: string | null;
  executedAt: string | null;
  error: string | null;
  declineReason?: string | null;
  createdAt: string;
  /** W2.5 independent reviewer verdict. */
  review?: ExpertActionReview | null;
  /** Set when the review call failed — human decides without it. */
  reviewError?: string | null;
  /** W2.5 request-info loop: the question sent to the user. */
  infoRequest?: {
    text: string;
    requestedBy: string | null;
    requestedAt: string | null;
  } | null;
}

/**
 * GET /admin/expert-chat/users/:userId/context — nested JSONB aggregates,
 * kept deliberately loose and rendered defensively (same policy as the
 * shadow-run transcript types above).
 */
export interface ExpertUserContext {
  user: {
    id: string;
    username: string | null;
    email: string | null;
    email_verified?: boolean;
    kyc_status?: string | null;
    created_at?: string;
    frozen_at?: string | null;
    freeze_reason?: string | null;
  };
  profile: any;
  wallet: any;
  kyc: any;
  predictions: any;
  ledger: any;
  payments: any;
  badges: any;
  tickets: any[];
  recentActions: any[];
}

export const apiService = new ApiService();
